import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subscription } from 'rxjs/index';
import { filter, map } from 'rxjs/operators';
import { AuthenticationSelectors } from '../../auth/state/authentication.selectors';
import { Person } from '../../core/model/central/person.model';
import { Strategy } from '../../core/model/central/strategy.model';
import { User } from '../../core/model/user.model';
import { LocationsService } from '../../locations/state/locations.service';
import { AppointmentListSelectors } from '../appointment-list/state/appointment-list.selectors';
import {
    AppointmentListService
} from '../appointment-list/state/appointment-list.service';
import { Appointment } from '../appointments/state/appointment.model';
import { StrategyService } from '../reminder-settings/state/strategy.service';
import { PatientAppointmentService } from './state/patient-appointment.service';
import { PatientsSelectors } from './state/patients.selectors';
import { PreviewEditTemplateActions } from '../../preview-edit-template/state/preview-edit-template.actions';

@Component({
    selector: 'patient-appointments',
    templateUrl: './patient-appointments.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class PatientAppointmentsComponent implements OnInit, OnDestroy {

    selectedPatient$: Observable<Person>;
    patientAppointments$: Observable<Array<Appointment>>;
    viewModel$: Observable<any>;
    loading$: Observable<boolean>;
    strategies$: Observable<Array<Strategy>>;
    subscriptions: Subscription = new Subscription();
    previousSelectedPatient: Person;
    queryFilter$: Observable<any>;
    currentUser$: Observable<User>;
    fromDate: Date = new Date();
    toDate: Date = new Date();
    personId = undefined;
    pipe = new DatePipe('en-US');

    constructor(private store: Store<any>,
                private strategyService: StrategyService,
                private appointmentListSelector: AppointmentListSelectors,
                private patientsSelectors: PatientsSelectors,
                private appointmentListService: AppointmentListService,
                private patientAppointmentService: PatientAppointmentService,
                private authenticationSelectors: AuthenticationSelectors,
                private locationsService: LocationsService

    ) {
        this.selectedPatient$ = this.patientsSelectors.selectSelectedPatient();
        this.patientAppointments$ = this.patientAppointmentService.entities$;
        this.loading$ = this.patientAppointmentService.refreshing$;
        this.strategies$ = this.strategyService.entities$;
        this.queryFilter$ = this.patientAppointmentService.queryFilter$;
        this.currentUser$ =  this.authenticationSelectors.selectCurrentUser();
        this.subscriptions.add(this.queryFilter$.
        pipe(
            filter(queryFilter => queryFilter !== undefined),
            filter(queryFilter => queryFilter.fromDate !== null)
        )
            .subscribe(
                queryFilter => {
                    let dateRange: any = {};
                    if (sessionStorage.getItem('appointmentByPatient') !== undefined &&
                        sessionStorage.getItem('appointmentByPatient') !== null) {
                        dateRange = JSON.parse(sessionStorage.getItem('appointmentByPatient'));
                    }

                    if (queryFilter.fromDate !== undefined || JSON.stringify(dateRange) !== '{}') {
                        this.fromDate = JSON.stringify(dateRange) !== '{}'
                            ? dateRange.fromDate : queryFilter.fromDate;
                        this.toDate = JSON.stringify(dateRange) !== '{}'
                            ? dateRange.toDate : new Date(queryFilter.toDate);
                    }
                    this.personId = queryFilter.personId;
                }
            ));
    }

    ngOnInit(): void {

        this.subscriptions.add(
            this.currentUser$
                .pipe(
                    filter(currentUser => currentUser !== undefined)
                )
                .subscribe(currentUser => {
                       this.store.dispatch(PreviewEditTemplateActions.getLocations({userId: currentUser.userId}));
                    }
                )
        );

        this.subscriptions.add(
            this.selectedPatient$
                .pipe(
                    filter(selectedPatient => selectedPatient !== undefined)
                )
                .subscribe((selectedPatient: Person) => {
                    this.personId = sessionStorage.getItem('previousSelectedPatient') !== null ? sessionStorage.getItem('previousSelectedPatient') : 0;
                    if (+this.personId !== selectedPatient.personId) {
                        if (typeof this.fromDate !== 'string') {
                            this.fromDate.setDate(this.fromDate.getDate() - 7);
                            this.toDate.setDate(this.toDate.getDate() + 7);
                        }
                        this.personId = selectedPatient.personId;
                    } else {
                        const getDateRange = JSON.parse(sessionStorage.getItem('appointmentByPatient'));
                        this.fromDate = getDateRange.fromDate;
                        this.toDate = getDateRange.toDate;
                    }

                    const fromFormat = this.pipe.transform(this.fromDate, 'yyyy-MM-dd');
                    const toFormat = this.pipe.transform(this.toDate, 'yyyy-MM-dd');

                    const filters = {
                        requestType: 'appointmentByPatientId',
                        personId: selectedPatient.personId.toString(),
                        fromDate: fromFormat.toString(),
                        toDate: toFormat.toString(),
                        offset: '0'
                    };

                    this.patientAppointmentService.clearCache();
                    this.patientAppointmentService.subscribeWebSocketJob(filters);
                    const dateRange = { fromDate: fromFormat.toString(), toDate: toFormat.toString() };
                    sessionStorage.setItem('appointmentByPatient', JSON.stringify(dateRange));
                    sessionStorage.setItem('previousSelectedPatient', selectedPatient.personId.toString());
                    this.appointmentListService.resetWebSocketJob({personIds: []});
            })
        );

        this.subscriptions.add(
            this.strategyService.loaded$
                .pipe(
                    filter(loaded => !loaded)
                )
                .subscribe(loaded => this.strategyService.getAll())

        );

        this.viewModel$ = combineLatest(
            [
            this.patientAppointments$]
        )
            .pipe(
                map(([patientAppointments]) =>
                    ({patientAppointments}))
            );

        this.appointmentListService.subscribeWebSocketJob({personIds: []});

    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        const fromFormat = this.pipe.transform(this.fromDate, 'yyyy-MM-dd');
        const toFormat = this.pipe.transform(this.toDate, 'yyyy-MM-dd');
        //reset the filter for next subscription
        const filters = {
            requestType: 'appointmentByPatientId',
            personId: '0',
            fromDate: fromFormat.toString(),
            toDate: toFormat.toString(),
            offset: '0'
        };

        this.patientAppointmentService.refreshWebSocketJob(filters);
        // Even if we unsubscribe the cache was not cleared
        this.patientAppointmentService.clearCache();
        this.patientAppointmentService.unsubscribeWebSocketJob();
        this.appointmentListService.resetWebSocketJob({personIds: []});
        this.appointmentListService.unsubscribeWebSocketJob();
    }

    getMoreAppointmentsOnScroll(totalLoaded): void {
        if (this.fromDate !== null && this.personId !== undefined && this.personId !== null)  {
            const fromFormat = this.pipe.transform(this.fromDate, 'yyyy-MM-dd');
            const toFormat = this.pipe.transform(this.toDate, 'yyyy-MM-dd');
            const filters = {
                requestType: 'appointmentByPatientId_scroll',
                personId: this.personId.toString(),
                fromDate: fromFormat.toString(),
                toDate: toFormat.toString(),
                offset: totalLoaded.toString()
            };

            this.patientAppointmentService.refreshWebSocketJob(filters);
            const dateRange = { fromDate: fromFormat.toString(), toDate: toFormat.toString() };
            sessionStorage.setItem('appointmentByPatient', JSON.stringify(dateRange));
        }
    }
}
